import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import type { WidgetConfig } from "shared";
import { DEFAULT_WIDGET_CONFIG } from "shared";

interface WidgetConfigContextValue {
  config: WidgetConfig;
  cssVariables: Record<string, string>;
  isDarkMode: boolean;
}

const WidgetConfigContext = createContext<WidgetConfigContextValue | null>(
  null,
);

interface WidgetConfigProviderProps {
  config: Partial<WidgetConfig> & { assistant_id: string; name: string };
  children: React.ReactNode;
}

/**
 * Provider for widget configuration and theming
 */
export function WidgetConfigProvider({
  config: partialConfig,
  children,
}: WidgetConfigProviderProps) {
  // Merge with defaults
  const config: WidgetConfig = useMemo(
    () => ({
      ...DEFAULT_WIDGET_CONFIG,
      ...partialConfig,
    }),
    [partialConfig],
  );

  // Handle system preference for 'auto' theme
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });

  useEffect(() => {
    if (config.theme !== "auto") return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [config.theme]);

  // Determine if dark mode should be active
  const isDarkMode = useMemo(() => {
    if (config.theme === "dark") return true;
    if (config.theme === "light") return false;
    return systemPrefersDark; // auto
  }, [config.theme, systemPrefersDark]);

  // Generate CSS variables for theming
  const cssVariables = useMemo(() => {
    // Helper to calculate if a color is light or dark (returns appropriate text color)
    const getContrastTextColor = (hexColor: string): string => {
      // Remove # if present
      const hex = hexColor.replace("#", "");
      // Parse RGB values
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // Calculate relative luminance (perceived brightness)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      // Return white for dark backgrounds, dark for light backgrounds
      return luminance > 0.5 ? "#18181b" : "#ffffff";
    };

    return {
      "--widget-primary": config.primary_color,
      "--widget-user-bubble": config.user_bubble_color,
      "--widget-user-bubble-text":
        config.user_bubble_text_color ||
        getContrastTextColor(config.user_bubble_color),
      "--widget-ai-bubble": config.ai_bubble_color,
      "--widget-ai-bubble-text":
        config.ai_bubble_text_color ||
        getContrastTextColor(config.ai_bubble_color),
      "--widget-admin-bubble": config.admin_bubble_color,
      "--widget-header-bg": config.header_bg_color,
      "--widget-header-text": config.header_text_color,
    };
  }, [config]);

  const value = useMemo(
    () => ({ config, cssVariables, isDarkMode }),
    [config, cssVariables, isDarkMode],
  );

  return (
    <WidgetConfigContext.Provider value={value}>
      <div
        className={isDarkMode ? "dark" : ""}
        style={cssVariables as React.CSSProperties}
      >
        {children}
      </div>
    </WidgetConfigContext.Provider>
  );
}

/**
 * Hook to access widget configuration
 */
export function useWidgetConfig() {
  const context = useContext(WidgetConfigContext);
  if (!context) {
    throw new Error(
      "useWidgetConfig must be used within a WidgetConfigProvider",
    );
  }
  return context;
}
